/**
 * Auto-Optimize Control + Monitor panel.
 *
 * One card, three responsibilities:
 *   1. Master toggles for the two runtime flags (auto-optimize +
 *      scraper gating). Toggles hit the runtime endpoints; the DB-backed
 *      flag cache picks the change up on its next poll (~5s max).
 *   2. Aggregate telemetry cards (paused / excluded sources, agents
 *      enabled, scraper-gating agents).
 *   3. Recent-activity feed drawn from ScrapeV2SourceUsername.
 *      qualityPauseHistory (pause / resume / rescore / exclude events
 *      with actor + reason).
 *
 * Polls /api/admin/auto-optimize/runtime every 10 s. Cheap: one indexed
 * aggregation for activity + two counts + the agent list.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  BoltIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const POLL_MS = 10_000;

const EVENT_STYLES = {
  paused: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  resumed: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  'manual-clear': 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  rescored: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
  excluded: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
};

function fmtRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const secs = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function fmtInt(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString();
}

function fmtPct(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${Number(v).toFixed(2)}%`;
}

function ToggleSwitch({ checked, disabled, onChange, label, subtitle, tone = 'indigo' }) {
  const trackClass = checked
    ? tone === 'orange'
      ? 'bg-orange-500'
      : 'bg-indigo-600'
    : 'bg-gray-300 dark:bg-gray-700';
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`w-full text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 flex items-center gap-3 transition ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-indigo-400 dark:hover:border-indigo-500'
      }`}
    >
      <div className="flex-1">
        <div className="text-sm font-semibold text-gray-900 dark:text-white">{label}</div>
        {subtitle && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</div>
        )}
      </div>
      <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${trackClass}`}>
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </div>
    </button>
  );
}

function StatCard({ label, value, hint, tone = 'default' }) {
  const toneClass =
    tone === 'orange'
      ? 'text-orange-600 dark:text-orange-400'
      : tone === 'red'
        ? 'text-red-600 dark:text-red-400'
        : tone === 'green'
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-gray-900 dark:text-white';
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
}

export default function AutoOptimizeControlPanel({ workspaceId = null }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);        // toggle key currently being flipped
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const pollRef = useRef(null);
  const abortRef = useRef(null);

  const actor = useMemo(() => {
    const uid = user?.id || user?._id;
    return uid ? `operator:${uid}` : 'operator:unknown';
  }, [user]);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const params = new URLSearchParams();
      if (workspaceId) params.set('workspaceId', workspaceId);
      params.set('activityLimit', '25');
      const url = `/admin/auto-optimize/runtime?${params.toString()}`;
      const { data } = await api.get(url, { signal: controller.signal });
      setState(data);
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
        // Silent poll failure is fine; only toast on hard load.
        if (!silent) toast.error(`Failed to load auto-optimize state: ${err?.message || err}`);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
    pollRef.current = window.setInterval(() => load({ silent: true }), POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  const flip = useCallback(async (patch, key, label) => {
    setBusy(key);
    try {
      await api.post('/admin/auto-optimize/runtime/flags', {
        ...patch,
        actor,
        reason: `Dashboard toggle by ${actor}`,
      });
      toast.success(`${label} ${patch[Object.keys(patch)[0]] ? 'enabled' : 'disabled'}`);
      await load({ silent: true });
    } catch (err) {
      toast.error(`Toggle failed: ${err?.response?.data?.error || err.message}`);
    } finally {
      setBusy(null);
    }
  }, [actor, load]);

  const refreshAll = useCallback(async () => {
    setRefreshBusy(true);
    try {
      const { data } = await api.post('/admin/auto-optimize/runtime/refresh-all', { actor });
      toast.success(`Kicked ${data?.kicked || 0} agent refresh${data?.kicked === 1 ? '' : 'es'}`);
      // Small delay so the writes land before we re-poll.
      window.setTimeout(() => load({ silent: true }), 1500);
    } catch (err) {
      toast.error(`Refresh-all failed: ${err?.response?.data?.error || err.message}`);
    } finally {
      setRefreshBusy(false);
    }
  }, [actor, load]);

  const flags = state?.featureFlags || {};
  const totals = state?.totals || {};
  const agents = state?.agents || [];
  const activity = state?.activity || [];

  const autoOn = Boolean(flags.autoOptimizeEnabled);
  const gateOn = Boolean(flags.scraperGatingEnabled);
  const envKilled = Boolean(flags.envKill?.autoOptimizeKilled || flags.envKill?.scraperGatingKilled);
  const status = envKilled
    ? { label: 'Env kill active', tone: 'red', Icon: ShieldExclamationIcon }
    : autoOn && gateOn
      ? { label: 'Fully armed', tone: 'green', Icon: CheckCircleIcon }
      : autoOn
        ? { label: 'Follow-side only', tone: 'orange', Icon: BoltIcon }
        : { label: 'Off', tone: 'default', Icon: XCircleIcon };

  return (
    <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
      <header
        className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer"
        onClick={() => setCollapsed((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <BoltIcon className={`w-5 h-5 ${autoOn ? 'text-indigo-600' : 'text-gray-400'}`} />
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              Auto-optimize
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Ranks sources by follow-back; pauses under-performers; auto-heals when they recover.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
              status.tone === 'green'
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                : status.tone === 'orange'
                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                  : status.tone === 'red'
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            }`}
          >
            <status.Icon className="w-3.5 h-3.5" />
            {status.label}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="text-xs text-gray-500 hover:text-indigo-600 inline-flex items-center gap-1"
            title="Refresh monitor"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {envKilled && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                An env kill-switch is active
                {flags.envKill?.autoOptimizeKilled ? ' (VA_AUTO_OPTIMIZE_KILL)' : ''}
                {flags.envKill?.scraperGatingKilled ? ' (VA_SCRAPER_GATING_KILL)' : ''}
                . Toggles will not take effect until ops clears the env var on the worker.
              </span>
            </div>
          )}

          {/* Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ToggleSwitch
              checked={autoOn}
              disabled={busy === 'auto' || flags.envKill?.autoOptimizeKilled}
              onChange={(v) => flip({ autoOptimizeEnabled: v }, 'auto', 'Auto-optimize')}
              label="Auto-optimize follow dispatch"
              subtitle="Top-N cohort + explore split · scheduler ranks every ~30 min"
            />
            <ToggleSwitch
              checked={gateOn}
              disabled={busy === 'gate' || flags.envKill?.scraperGatingKilled}
              onChange={(v) => flip({ scraperGatingEnabled: v }, 'gate', 'Scraper gating')}
              label="Scraper gating"
              subtitle="Skip scraping quality-paused / quality-excluded sources"
              tone="orange"
            />
          </div>

          {/* Aggregate stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatCard label="Agents enabled" value={fmtInt(totals.agentsEnabled)} />
            <StatCard label="Gating agents" value={fmtInt(totals.scraperGatingAgents)} />
            <StatCard label="Paused sources" value={fmtInt(totals.sourcesPaused)} tone="orange" />
            <StatCard label="Excluded" value={fmtInt(totals.sourcesExcluded)} tone="red" />
          </div>

          {/* Per-agent status */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Eligible agents ({agents.length}{agents.length ? ` · ${totals.agentsEnabled || 0} opted in` : ''})
              </div>
              <button
                type="button"
                onClick={refreshAll}
                disabled={refreshBusy || !autoOn || !totals.agentsEnabled}
                className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 hover:border-indigo-400 disabled:opacity-40"
                title={
                  !autoOn
                    ? 'Enable auto-optimize first'
                    : !totals.agentsEnabled
                      ? 'No agents opted in yet'
                      : 'Kick a refresh for every eligible agent right now'
                }
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 ${refreshBusy ? 'animate-spin' : ''}`} />
                Refresh all now
              </button>
            </div>
            {autoOn && agents.length > 0 && totals.agentsEnabled === 0 && (
              <div className="mb-2 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2 text-xs text-indigo-800 dark:text-indigo-200">
                System is armed but no agent is opted in yet. Open an agent below → <b>Follow automation settings</b> → <b>Auto optimize sources</b> drawer → toggle <b>Enabled</b>.
              </div>
            )}
            {agents.length ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Agent</th>
                      <th className="px-3 py-2">Metric</th>
                      <th className="px-3 py-2 text-right">Top</th>
                      <th className="px-3 py-2 text-right">Explore</th>
                      <th className="px-3 py-2 text-right">Cool-down</th>
                      <th className="px-3 py-2 text-right">Paused</th>
                      <th className="px-3 py-2 text-right">Excluded</th>
                      <th className="px-3 py-2">Last refresh</th>
                      <th className="px-3 py-2">Last rescore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a) => (
                      <tr
                        key={a.agentId}
                        className="border-t border-gray-100 dark:border-gray-800"
                      >
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center gap-1.5">
                            <span>{a.name || a.agentId.slice(-6)}</span>
                            {a.enabled ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                                on
                              </span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500">
                                off
                              </span>
                            )}
                            {a.scraperGating.enabled && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                                gate
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{a.metric}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtInt(a.topCount)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtInt(a.exploreCount)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtInt(a.coolDownCount)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-orange-600 dark:text-orange-400">
                          {fmtInt(a.scraperGating.pausedCount)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-red-600 dark:text-red-400">
                          {fmtInt(a.scraperGating.excludedCount)}
                        </td>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                          {fmtRelative(a.lastRefreshedAt)}
                        </td>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                          {a.scraperGating.enabled ? fmtRelative(a.scraperGating.lastRescoredAt) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-xs text-gray-500 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-3 py-4 text-center">
                No VA agents with a Scrape V2 pool target source. Create one below to make it eligible for auto-optimize.
              </div>
            )}
          </div>

          {/* Activity feed */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Recent activity
            </div>
            {activity.length ? (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto">
                {activity.map((ev, i) => (
                  <li
                    key={`${ev.username}-${ev.at}-${i}`}
                    className="px-3 py-2 flex items-center gap-2 text-xs"
                  >
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        EVENT_STYLES[ev.event] || 'bg-gray-100 dark:bg-gray-800 text-gray-600'
                      }`}
                    >
                      {ev.event || 'event'}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">@{ev.username}</span>
                    <span className="text-gray-500">
                      {ev.follows != null ? `${fmtInt(ev.follows)} follows` : null}
                      {ev.follows != null && ev.ratePct != null ? ' · ' : null}
                      {ev.ratePct != null ? fmtPct(ev.ratePct) : null}
                    </span>
                    <span className="flex-1" />
                    {ev.actor && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500">
                        {ev.actor}
                      </span>
                    )}
                    <span className="text-gray-400 tabular-nums whitespace-nowrap inline-flex items-center gap-1">
                      <ClockIcon className="w-3 h-3" />
                      {fmtRelative(ev.at)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-gray-500 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-3 py-4 text-center">
                No auto-optimize events yet. Actions appear here as they happen.
              </div>
            )}
          </div>

          {/* Footer meta */}
          <div className="text-[11px] text-gray-400 flex items-center justify-between">
            <span>
              Flag source: <code>{flags.sourcedFrom || '—'}</code>
              {flags.lastToggledBy ? ` · last toggle by ${flags.lastToggledBy}` : ''}
              {flags.lastToggledAt ? ` · ${fmtRelative(flags.lastToggledAt)}` : ''}
            </span>
            <span>Polling every {POLL_MS / 1000}s</span>
          </div>
        </div>
      )}
    </section>
  );
}
